import React, { useState, useEffect, useRef } from "react";
import { getOnboardingStats } from "../lib/inboxLib";
import { useSnackbar } from "../contexts/SnackbarContext";

interface OnboardingStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  inboxId: string;
}

const OnboardingStatsModal: React.FC<OnboardingStatsModalProps> = ({
  isOpen,
  onClose,
  inboxId,
}) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await getOnboardingStats(inboxId);
      setStats(data);
    } catch (error) {
      console.error("Failed to fetch onboarding stats:", error);
      showSnackbar("Failed to fetch onboarding stats", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && inboxId) {
      fetchStats();
    }
  }, [isOpen, inboxId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col"
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">
          Onboarding Stats
        </h2>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
              <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                {stats ? JSON.stringify(stats, null, 2) : "No data available"}
              </pre>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={fetchStats}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Refreshing..." : "Refresh"}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-700 rounded hover:bg-gray-400 dark:hover:bg-gray-600 dark:text-white"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingStatsModal;
